import { ServiceTypeOperationProcedureDialogStore } from './service-type-operation-procedure-dialog.store';
import { fb } from '../../../../../../modules/shared/shared.global-service';
import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { TextInputComponent } from '../../../../../kit/input/text-input/text-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { Validators } from '@angular/forms';
import { SelectInputComponent } from '../../../../../kit/input/select-input/select-input.component';
import { measurementUnitEnumOptions } from '@libs/types/src/common/enum/measurement-unit.enum';
import { CheckInputComponent } from '../../../../../kit/input/check-input/check-input.component';
import { DateInputComponent } from '../../../../../kit/input/date-input/date-input.component';
import { BaseDialogComponent } from '../../../../../kit/base-dialog/base-dialog.component';
import { dateify } from '../../../../../../modules/shared/helpers/date.helper';
import {
    SocialServiceOperationObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-operation.object';
import { winCarolCode } from '@libs/types/src/services/utils/win-carol-code.utils';
import { AppFormGroup, changeSignal } from '../../../../../../utils/form.utils';
import { isInvalid } from '../../../../../../modules/shared/helpers/validate';
import {
    SocialServiceOperationProcedureObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-operation-procedure.object';
import {
    SocialServiceOperationProcedureCreateInput,
} from '@libs/types/src/services/input-type/services/social-service/social-service-operation-procedure.create-input';
import {
    SocialServiceOperationProcedurePatchInput,
} from '@libs/types/src/services/input-type/services/social-service/social-service-operation-procedure.patch-input';
import { contentGlobalSuccess } from '../../../../../../modules/content/content.error';
import { TextAreaInputComponent } from '../../../../../kit/input/text-area-input/text-area-input.component';

@Component({
    selector: 'app-service-type-operation-procedure-dialog',
    templateUrl: './service-type-operation-procedure-dialog.component.html',
    styleUrl: './service-type-operation-procedure-dialog.component.scss',
    providers: [ServiceTypeOperationProcedureDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TextInputComponent,
        TranslatePipe,
        TooltipModule,
        SelectInputComponent,
        CheckInputComponent,
        DateInputComponent,
        TextAreaInputComponent,
    ],
})
export class ServiceTypeOperationProcedureDialogComponent extends BaseDialogComponent {

    public readonly store = inject(ServiceTypeOperationProcedureDialogStore);

    protected form = new AppFormGroup({
        name: fb.control('', Validators.required),
        code: fb.control<string | null>(null),
        description: fb.control<string | null>(null),
        unit: fb.control<string | null>(null),
        winCarolCode: fb.control<string | null>(null),
        customCode: fb.control<string | null>(null),
        payment: fb.control<boolean>(false),
        maxPrice: fb.control<string | null>(null),
        maxPriceExt: fb.control<string | null>(null),
        validFrom: fb.control<Date | null>(null),
        validTo: fb.control<Date | null>(null),
    });

    protected prefix = 'serviceType.operation.procedure.';

    protected codes = [1, 2, 3, 4, 5, 6, 6, 8, 9].map((it) => {
        const c = String(it);
        return {
            name: c,
            value: c,
        };
    });
    protected operation = signal<SocialServiceOperationObject | null>(null);
    protected procedure = signal<SocialServiceOperationProcedureObject | null>(null);
    protected codeChange = changeSignal(this.form.controls.code);
    protected customCodeAltered = false;
    protected customCodeChange = this.form.signalCodeChange('customCode');

    protected availableUnits = computed(() => {
        const op = this.operation();
        const opUnit = op?.unit;
        if (!opUnit) {
            return measurementUnitEnumOptions;
        }

        this.form.controls.unit.disable();
        this.form.patchValue({ unit: opUnit });

        return measurementUnitEnumOptions.filter((it) => it.value === opUnit);
    });

    constructor() {
        super();
        this.operation.set(this.dialogConfig.data.operation);

        effect(() => {
            if (this.customCodeAltered) return;
            // Ignore generation when code is already defined
            if (!this.dialogConfig.data.procedure?.winCarolCode) {
                this.form.patchValue({
                    customCode: winCarolCode(
                        this.store.lawCode(),
                        this.operation()?.type,
                        this.operation()?.billCode,
                        this.codeChange(),
                    ),
                }, { isCodeUpdate: true });
            }
        });

        effect(() => {
            const change = this.customCodeChange();
            if (change.value && !change.isCodeUpdate) {
                this.customCodeAltered = true;
            }
        });

        const procedure = this.dialogConfig.data.procedure;
        if (procedure) {
            this.procedure.set(procedure);
            this.form.patchValue({
                ...procedure,
                validFrom: dateify(procedure.validFrom),
                validTo: dateify(procedure.validTo),
            });
        }
    }

    public override async save(): Promise<void> {
        if (isInvalid(this.form)) return;

        const _data = {
            ...this.form.getRawValue(),
            description: this.form.value.description || '',
            validFrom: this.form.value.validFrom?.toISOString(),
            validTo: this.form.value.validTo?.toISOString(),
        };
        const id = this.procedure()?.id;
        if (id) {
            const data = _data as SocialServiceOperationProcedurePatchInput;
            data.id = id;
            await this.store.api.patchSocialServiceOperationProcedure(data, 'id');
            contentGlobalSuccess.next('common.updated');
        } else {
            const data = _data as SocialServiceOperationProcedureCreateInput;
            data.socialServiceOperationId = this.operation()!.id;
            await this.store.api.createSocialServiceOperationProcedure(data, 'id');
            contentGlobalSuccess.next('common.created');
        }

        this.dialogRef.close(true);
    }

}
